import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import cc from 'classcat';

interface IOptions {
    options: IOptionItem[];
    className?: string;
    optionsMenuClassName?: string;
    usePortal?: boolean;
}

export interface IOptionItem {
    label: string;
    color?: OptionColors;
    onClick: React.MouseEventHandler<HTMLButtonElement>;
}

type OptionColors = 'success' | 'error';

let currentOpenMenu: (() => void) | null = null;

export const Options = ({ options, className, optionsMenuClassName, usePortal = false }: IOptions) => {
    const [menuVisible, setMenuVisible] = useState(false);
    const [menuPosition, setMenuPosition] = useState({ bottom: 0, right: 0 });
    const buttonRef = useRef<HTMLButtonElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const closeMenu = () => {
        setMenuVisible(false);
        if (currentOpenMenu === closeMenu) {
            currentOpenMenu = null;
        }
    };

    const updateMenuPosition = () => {
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();

            setMenuPosition({
                bottom: window.innerHeight - rect.bottom,
                right: 0,
            });
        }
    };

    useEffect(() => {
        if (menuVisible && usePortal && buttonRef.current) {
            updateMenuPosition();

            const handleScroll = () => {
                updateMenuPosition();
            };

            window.addEventListener('scroll', handleScroll, true);
            window.addEventListener('resize', handleScroll);

            return () => {
                window.removeEventListener('scroll', handleScroll, true);
                window.removeEventListener('resize', handleScroll);
            };
        }
    }, [menuVisible, usePortal]);

    useEffect(() => {
        return () => {
            if (currentOpenMenu === closeMenu) {
                currentOpenMenu = null;
            }
        };
    }, []);

    const menuContent = menuVisible ? (
        <>
            <div onClick={closeMenu} className={cc(['opt-bubble-mask', { 'opt-bubble-mask--portal': usePortal }])} />
            <div
                className={cc([
                    'options__menu',
                    optionsMenuClassName,
                    {
                        'options__menu--fixed': usePortal,
                    },
                ])}
                style={
                    usePortal
                        ? {
                              bottom: `${menuPosition.bottom}px`,
                              right: `${menuPosition.right}px`,
                          }
                        : undefined
                }
            >
                {options.map(item => (
                    <button
                        className={cc(['options__item', item.color && `options__item--${item.color}`, 'paragraph-4'])}
                        key={item.label}
                        onClick={e => {
                            closeMenu();
                            item.onClick(e);
                        }}
                    >
                        {item.label}
                    </button>
                ))}
            </div>
        </>
    ) : null;

    return (
        <>
            <div ref={containerRef} className={cc(['options', className])}>
                <button
                    ref={buttonRef}
                    onClick={() => {
                        if (currentOpenMenu && currentOpenMenu !== closeMenu) {
                            currentOpenMenu();
                        }

                        setMenuVisible(prev => {
                            const newState = !prev;
                            if (newState) {
                                currentOpenMenu = closeMenu;
                            } else {
                                currentOpenMenu = null;
                            }
                            return newState;
                        });
                    }}
                    className={cc(['options__button', { 'options__button--active': menuVisible }])}
                >
                    <img src={menuVisible ? '/svg/3dots.svg' : '/svg/3dots-black.svg'} alt="Three dots" />
                </button>

                {usePortal && menuVisible ? createPortal(menuContent, document.body) : menuContent}
            </div>
        </>
    );
};
